import { BORDERS, COLORS, SPACING } from '@alto/design-library-tokens';
import React, { useEffect, useRef, useState } from 'react';
import { Animated, View } from 'react-native';
import styled from 'styled-components/native';
import { usePrevious } from '../../../hooks/usePrevious';
import { Row } from '../../containers';

const isTest = !!process.env.JEST_WORKER_ID;
const DEFAULT_ANIMATION_DURATION = 2000; // 2 seconds

const Step = styled(View)`
  flex-grow: 1;
  flex-direction: row;
  height: ${SPACING.STATIC.XXS.px};
  border-radius: ${BORDERS.RADIUS.DEFAULT.px};
  margin: 0 ${SPACING.STATIC.XXS.px};
`;

export type SteppedProgressBarProps = {
  readonly completedSteps: number;
  readonly totalSteps: number;
  readonly completedColor?: string;
  readonly incompleteColor?: string;
  readonly animationDuration?: number;
};

const IncompleteStep = styled(Step)<{ incompleteColor: SteppedProgressBarProps['incompleteColor'] }>`
  background-color: ${({ incompleteColor }) => incompleteColor};
`;

const CompletedStep = styled(Step)<{ completedColor: SteppedProgressBarProps['completedColor'] }>`
  background-color: ${({ completedColor }) => completedColor};
`;

export const SteppedProgressBar = ({
  completedSteps,
  totalSteps,
  completedColor = COLORS.BACKGROUND_COLORS.PROGRESS_BAR_COMPLETE,
  incompleteColor = COLORS.BACKGROUND_COLORS.PROGRESS_BAR_INCOMPLETE,
  animationDuration = DEFAULT_ANIMATION_DURATION,
}: SteppedProgressBarProps) => {
  const [animatedValue] = useState(() => new Animated.Value(0));
  const previousCompletedSteps = usePrevious(completedSteps);

  // Keep the latest previous-steps / duration in a ref so the animation only re-runs when
  // completedSteps changes (not when usePrevious updates a render later).
  const animationInputsRef = useRef({ previousCompletedSteps, animationDuration });
  useEffect(() => {
    animationInputsRef.current = { previousCompletedSteps, animationDuration };
  });

  useEffect(() => {
    if (isTest) return;

    const { previousCompletedSteps: prevSteps, animationDuration: duration } = animationInputsRef.current;
    if (prevSteps !== undefined && completedSteps <= prevSteps) {
      animatedValue.setValue(1);
    } else {
      animatedValue.setValue(0);
      Animated.timing(animatedValue, {
        toValue: 1,
        duration,
        useNativeDriver: false,
      }).start();
    }
  }, [animatedValue, completedSteps]);

  const steps = [];

  for (let step = 0; step < totalSteps; step++) {
    if (step === completedSteps - 1) {
      // If this was the last completed step, animate it from incomplete to complete
      steps.push(
        <IncompleteStep
          key={`stepped-progress-bar-animated-incomplete-step-${step}`}
          incompleteColor={incompleteColor}
          testID={`animated-incomplete-step-${step}`}
        >
          <Animated.View
            style={{
              flexGrow: animatedValue,
              borderRadius: BORDERS.RADIUS.DEFAULT.value,
              backgroundColor: completedColor || COLORS.PALETTE.SUCCESS.DEFAULT,
            }}
          />
        </IncompleteStep>,
      );
    } else if (step < completedSteps - 1) {
      steps.push(
        <CompletedStep
          key={`stepped-progress-bar-completed-step-${step}`}
          completedColor={completedColor}
          testID={`completed-step-${step}`}
        />,
      );
    } else {
      steps.push(
        <IncompleteStep
          key={`stepped-progress-bar-incomplete-step-${step}`}
          incompleteColor={incompleteColor}
          testID={`incomplete-step-${step}`}
        />,
      );
    }
  }

  return (
    <Row
      spaceBetween
      flexGrow={1}
    >
      {steps}
    </Row>
  );
};
